import { formatDayMonthYear } from '../../../../utils/date-only';
import { groupBy } from '../../../../utils/group-by';
import { plural } from '../../../../utils/plural';
import { Order, OrderPhoto, OrderStatus } from '../../orders-new/models/order.dto';

export interface OrderCardVm {
  id: number;
  name: string;
  orderNumber: string;
  /** "3 produkty" — root items, pluralized. */
  itemsLabel: string;
  /** Planned range once planned; "utworzono dd.mm.yyyy" while still NOWE. */
  dateLabel: string;
  /** 0..1 — same arithmetic as the planner bar, so the two views agree per order. */
  progressFraction: number;
  /** The order's faces (0081); the card shows the first, a click flips to the next (0085), none when empty. */
  photos: OrderPhoto[];
}

export interface OrderBoardColumn {
  header: string;
  orders: OrderCardVm[];
}

export interface OrderBoard {
  columns: OrderBoardColumn[];
}

// The board's four columns, in order. One source for the view model and for the page's loading
// branch, which draws the headers before the orders exist (0128); a renamed column cannot differ
// between them
export const BOARD_COLUMNS: { header: string; status: OrderStatus }[] = [
  { header: 'NOWE', status: OrderStatus.new },
  { header: 'ZAPLANOWANO', status: OrderStatus.planned },
  { header: 'W TRAKCIE', status: OrderStatus.inProgress },
  { header: 'ZAKOŃCZONO', status: OrderStatus.done }
];

export namespace OrderBoard {
  export function mapModelToViewModel(orders: Order[]): OrderBoard {
    const grouped = groupBy(orders, o => o.status);

    return {
      columns: BOARD_COLUMNS.map(({ header, status }) => ({
        header,
        orders: (grouped[status] ?? []).map(mapCard)
      }))
    };
  }

  function mapCard(order: Order): OrderCardVm {
    return {
      id: order.id,
      name: order.name,
      orderNumber: order.orderNumber,
      itemsLabel: `${order.itemCount} ${plural(order.itemCount, 'produkt', 'produkty', 'produktów')}`,
      dateLabel: mapDateLabel(order),
      // counted in parts, 1 exactly when Done (0112)
      progressFraction: order.progressFraction,
      photos: order.photos
    };
  }

  function mapDateLabel(order: Order): string {
    if (order.plannedStartDate && order.plannedEndDate) {
      return `${formatDayMonthYear(order.plannedStartDate)} – ${formatDayMonthYear(order.plannedEndDate)}`;
    }

    // createdAt is a full timestamp; the date-only helpers want yyyy-mm-dd
    return `utworzono ${formatDayMonthYear(order.createdAt.slice(0, 10))}`;
  }
}
